/** The stack of cards under the staff edit header, wired to the two field
 *  renderers so the sections do not each import them. */
import { History } from 'lucide-react';

import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../types/permissions';
import StaffPayRatesCard from '../pay-rates/StaffPayRatesCard';
import StaffAuditLog from '../StaffAuditLog';
import StaffProfileSection, { type StaffProfileSectionProps } from '../StaffProfileSection';
import StaffWorkshopHistory from '../StaffWorkshopHistory';

import StaffDisplayField from './StaffDisplayField';
import StaffEditableField from './StaffEditableField';

export default function StaffEditBody({
  member,
  isEditing,
  regions,
  fieldErrors,
  valueOf,
  setField,
  onSalariedChanged,
}: Omit<StaffProfileSectionProps, 'EditableField' | 'DisplayField'> & {
  onSalariedChanged: () => void;
}) {
  const { hasPermission } = usePermissions();
  return (
    <div className="space-y-6">
      <StaffProfileSection
        member={member}
        isEditing={isEditing}
        regions={regions}
        fieldErrors={fieldErrors}
        valueOf={valueOf}
        setField={setField}
        EditableField={StaffEditableField}
        DisplayField={StaffDisplayField}
      />

      {hasPermission(Permission.READ_PAY_RATES) && (
        <StaffPayRatesCard
          userId={member.id}
          memberName={member.name}
          onSalariedChanged={onSalariedChanged}
        />
      )}

      <StaffWorkshopHistory workshops={member.workshops ?? []} />

      <section className="bg-white dark:bg-[#1e2521] rounded-2xl border border-stone-200 dark:border-stone-700 p-5">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-bark-900 dark:text-stone-100 mb-4">
          <History size={14} className="text-moss-600 dark:text-moss-400" />
          Edit history
        </h2>
        <StaffAuditLog
          audit={(member.audit ?? []).map((a) => ({
            id: a.id,
            actorUserId: a.actorUserId,
            actorEmail: a.actorEmail,
            changedAt: a.changedAt,
            fieldChanges: a.fieldChanges,
          }))}
        />
      </section>
    </div>
  );
}
